import { z } from "zod";
import type { ApiEnv } from "./api-client";
import type { Env as QueryEnv } from "@mozioinc/query";

const optionalString = z.string().optional().default("");

const envSchema = z.object({
  // Set explicitly per environment; neither Cloudflare nor Vercel injects it.
  DEPLOY_ENV: z.enum(["production", "preview", "development"]),
  GIT_COMMIT_SHA: optionalString,
  MOZIO_PUBLIC_STRIPE_API_KEY: z.string(),
  MOZIO_PUBLIC_SENTRY_DSN: z.string().optional(),
  MOZIO_PUBLIC_POSTHOG_KEY: z.string(),
  MOZIO_PUBLIC_SENTRY_ORGANIZATION: z.string(),
  MOZIO_PUBLIC_SENTRY_PROJECT_ID: z.coerce.number(),
  MOZIO_PUBLIC_ENABLE_MOCKS: z
    .enum(["true", "false"])
    .transform((value: string) => value === "true"),
  MOZIO_PUBLIC_ZENDESK_KEY: z.string().optional(),
  MOZIO_PUBLIC_INTERCOM_APP_ID: z.string(),
  MOZIO_PUBLIC_STREAM_CHAT_API_KEY: z.string(),
  MOZIO_PUBLIC_GOOGLE_MAPS_API_KEY: z.string().default(""),
});

type Envs = z.infer<typeof envSchema> & {
  API_ENV: ApiEnv;
  ASSET_BASE_URL: string;
  QUERY_ENV: QueryEnv;
  FIRELINKS_BASE_URL: string;
};

let envMemo: Envs;

export const getEnvs = (): Envs => {
  if (envMemo) return envMemo;
  const envs = envSchema.parse(import.meta.env);
  const isProduction = envs.DEPLOY_ENV === "production";

  envMemo = {
    ...envs,
    GIT_COMMIT_SHA: envs.GIT_COMMIT_SHA.trim() || "local",
    API_ENV: isProduction ? "production" : "staging",
    ASSET_BASE_URL: "",
    QUERY_ENV: isProduction ? "production" : "staging",
    FIRELINKS_BASE_URL: isProduction
      ? "https://links.mozio.com"
      : "https://links-staging.mozio.com",
  };

  return envMemo;
};
